import axios from '@/axiosConnection'
import toastr from '@/toastr'

export default {
  namespaced: true,
  state: {
    items: [],
    copy: {},
    types: [{
        value: "renting",
        text: "Location"
      },
      {
        value: "selling",
        text: "Vente"
      },
      {
        // SFD ESM 2.10 : matériel loué chez un tiers pour le compte d'une agence. Ni stock,
        // ni référence unique, ni entrepôt, ni grille tarifaire — le prix est négocié demande
        // par demande. Administrable uniquement depuis « Produits spéciaux ».
        value: "location_for_account",
        text: "Location pour compte"
      }
    ],
    // Jours facturables d'un produit de location pour compte, au format ISO-8601 (1 = lundi).
    // Certains matériels ne sont pas facturés tous les jours : une nacelle facturée le samedi
    // mais pas le dimanche. Rien de coché = les sept jours.
    billable_days: [
      { value: 1, text: "Lundi" },
      { value: 2, text: "Mardi" },
      { value: 3, text: "Mercredi" },
      { value: 4, text: "Jeudi" },
      { value: 5, text: "Vendredi" },
      { value: 6, text: "Samedi" },
      { value: 7, text: "Dimanche" }
    ],
    parent_product_types: [{
      value: "product_with_variations",
      text: "Produit avec déclinaisons"
      },
      {
        value: "product_lot",
        text: "Lot de produits"
      },
    ],
    child_product_types: [{
        value: "product_variation",
        text: "Déclinaison"
      },
      {
        value: "product_in_lot",
        text: "Dans un lot"
      }
    ],
    parent_product_types_by_child_product_type: {
      product_variation: "product_with_variations",
      product_in_lot: "product_lot",
    },
    // Libellés de repli, calés sur la grille Axiloc historique (price_grid_range).
    // Les prix rattachés à une grille (ESM) portent leur propre libellé : les seuils
    // ESM diffèrent (31-90, 91-365, 366 j-4 ans, > 4 ans) et ces libellés-ci seraient faux.
    price_ranges: [{
        value: "range1",
        text: "du 1er au 30e jour",
        day_start: 1
      },
      {
        value: "range2",
        text: "du 31e au 60e jour",
        day_start: 31
      },
      {
        value: "range3",
        text: "du 61e jour à la 4e année",
        day_start: 61
      },
      {
        value: "range4",
        text: "au-delà de la 4e année",
        day_start: 1462
      },
      {
        value: "range5",
        text: "au-delà de 4 ans",
        day_start: 1462
      },
    ],
    // Ordre d'affichage des grilles : la grille en vigueur d'abord, l'historique ensuite.
    price_grids_order: ["esm", "mcs", "axiloc"],
    price_grid_default_label: "Grille Axiloc (historique)",
    return_modes: [{
        value: "chronopost",
        text: "Chronopost"
      },
      {
        value: "transporter",
        text: "Transporteur"
      }
    ],
  },
  mutations: {
    prepareCopy(state, copy) {
      state.copy = copy
    },
    emptyCopy(state) {
      state.copy = {}
    }
  },
  getters: {
    getAll: state => state.items,
    getAllByType: state => type => state.items.filter(item => item.type == type),
    getAllWithParentType: state => state.items.filter(item => item.parent_product_type != null),
    getAllByParentType: state => type => type ? state.items.filter(item => item.parent_product_type == type) : [],
    getByStatusSlug: state => status => state.items.filter(item => item.status_slug == status || (item.status && item.status.status_slug == status)),
    getItem: state => id => state.items.find(item => item.id == id),
    getItemByInternalCode: state => internal_code => state.items.find(item => item.internal_code == internal_code),
    getItemByBarCode: state => bar_code => state.items.find(item => item.custom_bar_code == bar_code || item.internal_code == bar_code),
    getTypes: state => state.types,
    // Le catalogue principal ne connaît que Location et Vente : la nature « Location pour
    // compte » ne s'administre que depuis « Produits spéciaux ».
    getCatalogTypes: state => state.types.filter(type => type.value !== 'location_for_account'),
    getBillableDaysForSelect: state => state.billable_days,
    getType: state => type => state.types.find(item => item.value == type),
    getPriceRanges: state => state.price_ranges,
    getPriceRange: state => price_range => state.price_ranges.find(item => item.value == price_range),
    /**
     * Prix actifs regroupés par grille tarifaire, prêts à l'affichage.
     *
     * Un produit peut porter deux jeux de prix (Axiloc historique + ESM) : sans
     * regroupement, les paliers apparaissent en double et avec les mauvais libellés.
     * Chaque groupe est trié par seuil de palier, de sorte que le dernier palier
     * (« au-delà de 4 ans ») figure toujours en fin de liste.
     *
     * @returns [{ code, label, prices: [{ id, label, price_value, is_last }] }]
     */
    getPriceGroups: state => prices => {
      const rangeIndex = value => state.price_ranges.findIndex(range => range.value == value)
      const groups = []

      ;(prices || [])
        .filter(price => !price.price_end_date)
        .filter(price => rangeIndex(price.price_range) > -1)
        .forEach(price => {
          const code = price.grid_code || "axiloc"
          let group = groups.find(item => item.code == code)
          if (!group) {
            group = {
              code: code,
              label: price.grid_label || state.price_grid_default_label,
              prices: []
            }
            groups.push(group)
          }

          const fallback = state.price_ranges[rangeIndex(price.price_range)]
          group.prices.push({
            id: price.id,
            price_range: price.price_range,
            label: price.range_label || fallback.text,
            day_start: price.day_start || fallback.day_start,
            price_value: price.price_value
          })
        })

      groups.forEach(group => {
        group.prices.sort((a, b) => a.day_start - b.day_start)
        // Dernier palier (sans borne haute) : mis en avant à part, c'est celui qui
        // manquait à l'affichage.
        group.prices.forEach((price, index) => {
          price.is_last = index === group.prices.length - 1 && group.prices.length > 1
        })
      })

      return groups.sort((a, b) => {
        const orderA = state.price_grids_order.indexOf(a.code)
        const orderB = state.price_grids_order.indexOf(b.code)

        return (orderA < 0 ? state.price_grids_order.length : orderA) - (orderB < 0 ? state.price_grids_order.length : orderB)
      })
    },
    getReturnModes: state => state.return_modes,
    getReturnMode: state => return_mode => state.return_modes.find(item => item.value == (return_mode || "").toLowerCase()),
    getParentType: state => type => state.parent_product_types.find(item => item.value == type),
    getChildType: state => type => state.child_product_types.find(item => item.value == type),
    getParentTypesForSelect: state => state.parent_product_types,
    getChildTypesForSelect: state => state.child_product_types,
    getParentTypeByChildType: state => type => state.parent_product_types_by_child_product_type[type],
    getCopy: state => state.copy
  },
  actions: {

    /**
     * Charge tous les items via l'API dans le state. Si des données sont déjà présentes dans le state, le résolve() est lancé de suite
     * @param   {boolean} wait  Si wait = true, on attends vraiment que les données soient reçues pour lancer le resolve() (impératif pour charger un formulaire de modification)
     * @returns {promise}       Aucune donnée n'est retournée
     */
    getAll: (context, wait = false) => {
      let params = {
        wait: wait,
        api_url: 'product',
        api_data_path: 'products',
        namespace: 'product'
      }
      return context.dispatch('getAll', params, {
        root: true
      })
    },
    /**
     * Charge tous les items via l'API dans le state avec des données limitées. Si des données sont déjà présentes dans le state, le résolve() est lancé de suite
     * @param   {boolean} wait  Si wait = true, on attends vraiment que les données soient reçues pour lancer le resolve() (impératif pour charger un formulaire de modification)
     * @param   {boolean} merge Si merge = true, une fiche complète déjà chargée (prix, références…) n'est pas
     *                          écrasée par sa version simplifiée
     * @returns {promise}       Aucune donnée n'est retournée
     */
    getAllForSelect: (context, options = false) => {
      const { wait = false, merge = false } = typeof options === 'object' && options !== null ? options : { wait: options }
      let params = {
        wait: wait,
        merge: merge,
        api_url: 'product/index/simple',
        api_data_path: 'products',
        namespace: 'product'
      }
      return context.dispatch('getAll', params, {
        root: true
      })
    },
    /**
     * AC-002 — Charge les produits correspondant à une recherche, sans vider le state.
     * Alternative à getAllForSelect quand le catalogue entier (> 4 000 produits) est trop
     * lourd pour une liste déroulante.
     * @param   {string}  search      Expression recherchée
     * @param   {string}  statusSlug  Statut produit à imposer (ex. 'product_on_sale')
     * @param   {string}  offerType   Nature d'offre à imposer (champ Type : 'selling', 'renting'…)
     * @param   {boolean} add         true pour ajouter au state au lieu de le remplacer
     * @returns {promise}
     */
    getAllForSelectBySearch: (context, { wait = false, search, statusSlug = null, offerType = null, add = true }) => {
      let query = 'search=' + encodeURIComponent(search)
      if (statusSlug) {
        query += '&statusSlug=' + statusSlug
      }
      if (offerType) {
        query += '&type=' + encodeURIComponent(offerType)
      }
      let params = {
        wait: wait,
        api_url: 'product/index/simple',
        api_data_path: 'products',
        namespace: 'product',
        query: query,
        add: add
      }
      return context.dispatch('getAll', params, {
        root: true
      })
    },
    /**
     * AC-002 — Charge des produits par identifiants, quel que soit leur statut : sert à
     * réafficher une sélection enregistrée, y compris ses entrées devenues indisponibles.
     * @param   {array}   ids   Identifiants produits
     * @param   {boolean} add   true pour ajouter au state au lieu de le remplacer
     * @returns {promise}
     */
    getAllForSelectByIds: (context, { wait = false, ids, add = true }) => {
      if (!ids || !ids.length) {
        return Promise.resolve()
      }
      let params = {
        wait: wait,
        api_url: 'product/index/simple',
        api_data_path: 'products',
        namespace: 'product',
        query: 'ids=' + ids.join(','),
        add: add
      }
      return context.dispatch('getAll', params, {
        root: true
      })
    },
    /**
     * Retourne tous les items dont le stock mini est atteint via l'API.
     * @returns {promise}       Aucune donnée n'est retournée
     */
    getMinimumStockReached: (context, type) => {
      var params = null;
      if(type){
        params += '&type='+type
      }
      return new Promise(function (resolve, reject) {
        axios.get("product/minimun/stock/reached?limit=0"+params)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            toastr.error("Le chargement de la liste a échoué")
            reject(error)
          })
      })
    },
    getItem: (context, {
      id,
      wait = false
    }) => {
      let params = {
        wait: wait,
        api_url: 'product',
        namespace: 'product',
        id: id
      }
      return context.dispatch('getItem', params, {
        root: true
      })
    },
    addItem(context, item) {
      let params = {
        api_url: 'product',
        namespace: 'product',
        item: item
      }
      return context.dispatch('addItem', params, {
        root: true
      })
    },
    saveItem(context, item) {
      let params = {
        api_url: 'product',
        namespace: 'product',
        item: item
      }
      return context.dispatch('saveItem', params, {
        root: true
      })
    },
    deleteItem(context, id) {
      let params = {
        api_url: 'product',
        namespace: 'product',
        id: id
      }
      return context.dispatch('deleteItem', params, {
        root: true
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/modules/product.js